import { FC } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "shared/components/Spor";
import type { ReserveAlternativeTransport as ReserveVehicle } from "shared/types/alternativeTransport";
import { getPrintableFromVehicleType } from "shared/utils/alternativeTransport";
import { TimeInterval } from "shared/components/dataDisplay/TimeInterval";
import { SMHeading, SMLabel } from "shared/components/typography/TitleStyles";
import { CallButton } from "shared/components/buttons/CallButton";
import { INLINE_ACTIONS_MIN_WIDTH } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import styled from "styled-components";

type Props = {
  vehicle: ReserveVehicle;
};

/**
 * Backup bus header content.
 */
const ItineraryGrid = styled.div`
  display: grid;
  width: 100%;
  margin-top: -${tokens.default.size.spacing[1.5]};
  column-gap: ${tokens.default.size.spacing[2]};
  row-gap: 3px;
  align-items: stretch;
  grid-template-columns: auto minmax(0, 1fr) auto minmax(0, 1fr);
  grid-template-rows: repeat(3, minmax(min-content, 1fr));
  grid-template-areas:
    "operator     operator     location   location"
    "vehicle      vehicle      baseStop   baseStop"
    "availability availability reference  reference";

  @container module (width >= ${INLINE_ACTIONS_MIN_WIDTH}) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    grid-template-rows: minmax(min-content, 1fr);
    grid-template-areas: "operator vehicle availability location baseStop reference";
  }
`;

const Field = styled.div`
  display: grid;
  grid-template-columns: subgrid;
  align-items: center;
  align-content: center;
  min-width: 0;

  @container module (width >= ${INLINE_ACTIONS_MIN_WIDTH}) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: ${tokens.default.size.spacing[0.5]};
  }
`;

export const BackupAlternativeTransportItinerary: FC<Props> = ({
  vehicle,
}: Props) => {
  const { t } = useTranslation();

  return (
    <ItineraryGrid>
      <Field style={{ gridArea: "operator" }}>
        <SMHeading>{vehicle.operatorName}</SMHeading>
        {vehicle.driverPhoneNumber ? (
          <CallButton phoneNumber={vehicle.driverPhoneNumber} size="xs" />
        ) : (
          <SMLabel>{t("Ingen telefonnummer")}</SMLabel>
        )}
      </Field>

      <Field style={{ gridArea: "vehicle" }}>
        <SMHeading>{t("Kjøretøy")}</SMHeading>
        <SMLabel>
          {getPrintableFromVehicleType(vehicle.type, "sentence")}
        </SMLabel>
      </Field>

      <Field style={{ gridArea: "availability" }}>
        <SMHeading>{t("Tilgjengelighet")}</SMHeading>
        <SMLabel as="div">
          <TimeInterval
            startTime={vehicle.shiftStartTime}
            endTime={vehicle.shiftEndTime}
          />
        </SMLabel>
      </Field>

      <Field style={{ gridArea: "location" }}>
        <SMHeading>{t("Lokasjon")}</SMHeading>
        <SMLabel>{vehicle.stopName}</SMLabel>
      </Field>

      <Field style={{ gridArea: "baseStop" }}>
        <SMHeading>{t("Stasjon")}</SMHeading>
        <SMLabel>{vehicle.baseStopName}</SMLabel>
      </Field>

      <Field style={{ gridArea: "reference" }}>
        <SMHeading>{t("Referanse")}</SMHeading>
        <SMLabel>{vehicle.isirkId || vehicle.referenceNumber}</SMLabel>
      </Field>
    </ItineraryGrid>
  );
};
